import { useEffect, useMemo } from "react";
import {
    Button,
    createStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Theme,
    Typography,
    withStyles,
    WithStyles
} from "@material-ui/core";
import { AddCircleOutline, OpenInNew, Publish } from "@material-ui/icons";
import { enqueueSnackbar } from "notistack";
import { useHistory } from "react-router";
import { Link, LinkProps } from "react-router-dom";
import {
    useGetAuctionsQuery,
    usePublishAuctionsMutation
} from "../../../api/auctionApiSlice";
import { formatAuctionDateTime } from "../../../helpers/auctionDateTime";
import IAuction from "../../../models/auction/IAuction";
import * as Roles from "../../../models/security/Roles";
import { userHasRole } from "../../../providers/SecurityProvider";
import { useAppSelector } from "../../../store/hooks";
import FullscreenLoader from "../../layout/FullscreenLoader";
import SectionBanner from "../../shared/SectionBanner";
import Constants from "./../../../models/utility/Constants";
import {
    sortAuctionByDateAscendingUnscheduledLast,
    sortAuctionByDateDescending
} from "./../../../models/utility/Sorting";

const styles = (theme: Theme) =>
    createStyles({
        buttonCreateNew: { float: "right" },
        editButton: { padding: theme.spacing() },
        noAuctionsText: { margin: theme.spacing(3) },
        paper: {
            alignItems: "center",
            display: "flex",
            flexDirection: "column"
        },
        section: {
            [theme.breakpoints.down("xs")]: {
                paddingLeft: theme.bodyContent.mobilePadding,
                paddingRight: theme.bodyContent.mobilePadding
            },
            backgroundColor: theme.palette.background.paper,
            paddingLeft: theme.bodyContent.desktopPadding,
            paddingRight: theme.bodyContent.desktopPadding,
            position: "relative",
            width: "100%"
        },
        sectionContent: {
            [theme.breakpoints.down("xs")]: {
                paddingBottom: theme.bodyContent.mobilePadding,
                paddingTop: theme.bodyContent.mobilePadding
            },
            margin: "0 auto",
            maxWidth: theme.bodyContent.maxWidth,
            paddingBottom: theme.bodyContent.desktopPadding,
            paddingTop: theme.bodyContent.desktopPadding,
            width: "100%"
        },
        statusLabel: {
            display: "inline-block",
            padding: `3px 6px`,
            whiteSpace: "nowrap"
        },
        statusLabelClosed: {
            backgroundColor: theme.palette.error.main,
            color: theme.palette.error.contrastText
        },
        statusLabelDraft: {
            backgroundColor: theme.palette.grey[400],
            color: theme.palette.common.white
        },
        statusLabelLive: {
            backgroundColor: theme.palette.success.main,
            color: theme.palette.success.contrastText
        },
        statusLabelNoSale: {
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.warning.contrastText
        },
        statusLabelUpcoming: {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText
        },
        table: {
            marginBottom: theme.spacing(5),
            width: "100%"
        },
        tableCell: { borderColor: theme.palette.divider },
        tableCellButton: {
            borderColor: theme.palette.divider,
            paddingLeft: theme.spacing(),
            paddingRight: 0
        },
        tableCellHeader: { borderColor: "transparent" },
        tableHeader: { fontWeight: "bold" },
        tableLast: { marginBottom: 0 },
        tableRow: {
            "&:nth-of-type(odd)": {
                backgroundColor: theme.palette.background.default
            },
            "&:hover": {
                backgroundColor: theme.palette.primary.light,
                cursor: "pointer"
            },
            borderColor: theme.palette.grey[500]
        },
        buttonToolbar: {
            float: "right",
            "& > *": {
                marginRight: theme.spacing(2), // "16px",
                "&:last-child": {
                    marginRight: "0"
                }
            }
        }
    });

type ManageAuctionListProps = WithStyles<typeof styles>;

function ManageAuctionList({ classes }: ManageAuctionListProps) {
    const user = useAppSelector((state) => state.userStoreState.user);
    const history = useHistory();

    useEffect(() => {
        Constants.SetDocumentTitle("Admin - Manage Sales");
    }, []);

    const includeTestAuctions = userHasRole(Roles.Developer)(user);

    const {
        data: auctions,
        error,
        isLoading
    } = useGetAuctionsQuery({
        includeDrafts: true,
        includeTestAuctions
    });
    const [publishAuctions, { isLoading: isPublishing }] =
        usePublishAuctionsMutation();
    const { onSale, drafts, pastSales } = useMemo(() => {
        const live: IAuction[] = [];
        const upcoming: IAuction[] = [];
        const drafts: IAuction[] = [];
        const pastSales: IAuction[] = [];

        for (const auction of auctions ?? []) {
            if (auction.isNoSale || auction.isCompleted) {
                pastSales.push(auction);
            } else if (auction.isLive) {
                live.push(auction);
            } else if (auction.draftMode) {
                drafts.push(auction);
            } else {
                upcoming.push(auction);
            }
        }

        return {
            onSale: [
                ...[...live].sort(sortAuctionByDateAscendingUnscheduledLast),
                ...[...upcoming].sort(sortAuctionByDateAscendingUnscheduledLast)
            ],
            drafts: [...drafts].sort(sortAuctionByDateAscendingUnscheduledLast),
            pastSales: [...pastSales].sort(sortAuctionByDateDescending)
        };
    }, [auctions]);

    const columnCount = includeTestAuctions ? 6 : 5;

    const onClickPublishButton = async () => {
        try {
            await publishAuctions().unwrap();
            enqueueSnackbar(
                'Publishing started. Click "View Deploys" to see progress..',
                {
                    anchorOrigin: { horizontal: "left", vertical: "top" },
                    preventDuplicate: false,
                    variant: "success",
                    persist: false
                }
            );
        } catch {
            enqueueSnackbar("Error publishing...", {
                anchorOrigin: { horizontal: "left", vertical: "top" },
                preventDuplicate: false,
                variant: "error",
                persist: false
            });
        }
    };

    const renderAuctionStatus = (auction: IAuction) => {
        let labelClasses = classes.statusLabel;
        let labelText = "";

        if (auction.isNoSale) {
            labelClasses += ` ${classes.statusLabelNoSale}`;
            labelText = "No Sale";
        } else if (auction.isCompleted) {
            labelClasses += ` ${classes.statusLabelClosed}`;
            labelText = "Closed";
        } else if (auction.isLive) {
            labelClasses += ` ${classes.statusLabelLive}`;
            labelText = "LIVE!";
        } else if (auction.draftMode) {
            labelClasses += ` ${classes.statusLabelDraft}`;
            labelText = "Draft";
        } else {
            labelClasses += ` ${classes.statusLabelUpcoming}`;
            labelText = "Upcoming";
        }

        return <div className={labelClasses}>{labelText}</div>;
    };

    const renderAuctionDate = (auctionDate: string) => {
        const dateTime = formatAuctionDateTime(auctionDate);
        return dateTime ? `${dateTime.date} at ${dateTime.time}` : "";
    };

    const renderAuctionRow = (auction: IAuction) => {
        return (
            <TableRow
                key={auction.auctionId}
                className={classes.tableRow}
                onClick={() => handleClick(auction.url)}
            >
                <TableCell className={classes.tableCell}>
                    {auction.name}
                </TableCell>
                <TableCell className={classes.tableCell}>
                    {auction.url}
                </TableCell>
                <TableCell className={classes.tableCell}>
                    {auction.auctionDate === Constants.UnscheduledTime ? (
                        <Typography color="textPrimary">Unscheduled</Typography>
                    ) : (
                        renderAuctionDate(auction.auctionDate)
                    )}
                </TableCell>
                <TableCell className={classes.tableCell}>
                    {auction.parcelCount || 0}
                </TableCell>
                {includeTestAuctions && (
                    <TableCell className={classes.tableCell}>
                        {auction.isTest ? "Yes" : "No"}
                    </TableCell>
                )}
                <TableCell className={classes.tableCell}>
                    {renderAuctionStatus(auction)}
                </TableCell>
            </TableRow>
        );
    };

    const renderColumnHeaders = () => (
        <TableRow>
            <TableCell className={classes.tableCell}>Name</TableCell>
            <TableCell className={classes.tableCell}>URL</TableCell>
            <TableCell className={classes.tableCell}>Start Date</TableCell>
            <TableCell className={classes.tableCell}>Parcels</TableCell>
            {includeTestAuctions && (
                <TableCell className={classes.tableCell}>Test Mode</TableCell>
            )}
            <TableCell className={classes.tableCell}>Status</TableCell>
        </TableRow>
    );

    const renderAuctionRows = (rows: IAuction[], emptyText: string) => (
        <TableBody>
            {rows.length === 0 && (
                <TableRow>
                    <TableCell colSpan={columnCount}>
                        <Typography
                            className={classes.noAuctionsText}
                            component="div"
                            variant="body2"
                            align="center"
                            color="textSecondary"
                        >
                            {emptyText}
                        </Typography>
                    </TableCell>
                </TableRow>
            )}
            {rows.map((auction) => renderAuctionRow(auction))}
        </TableBody>
    );

    const renderSimpleTable = (
        title: string,
        rows: IAuction[],
        emptyText: string,
        isLast = false
    ) => (
        <Paper>
            <Table
                className={`${classes.table} ${isLast ? classes.tableLast : ""}`}
            >
                <TableHead>
                    <TableRow>
                        <TableCell
                            className={classes.tableCellHeader}
                            colSpan={columnCount}
                        >
                            <Typography
                                className={classes.tableHeader}
                                variant="subtitle1"
                            >
                                {title}
                            </Typography>
                        </TableCell>
                    </TableRow>
                    {renderColumnHeaders()}
                </TableHead>
                {renderAuctionRows(rows, emptyText)}
            </Table>
        </Paper>
    );

    const auctionCreateLink = (props: Partial<LinkProps>) => (
        <Link
            to={Constants.Routes.AdminAuctionDetail(Constants.UrlCreateAuction)}
            {...props}
        />
    );

    const viewDeploysLink = (props: Partial<LinkProps>) => (
        <Link
            target="_blank"
            to={Constants.Routes.AdminAuctionDetail(Constants.ViewDeploys)}
            {...props}
        />
    );

    const handleClick = (auctionUrl: string) => {
        history.push(Constants.Routes.AdminAuctionDetail(auctionUrl));
    };

    if (isLoading) {
        return <FullscreenLoader />;
    }

    if (error) {
        return (
            <Paper className={classes.paper} elevation={2}>
                <SectionBanner leftSideText="Admin - Manage Sales" />
                <Typography color="error" style={{ margin: 24 }}>
                    An error occurred while loading sales.
                </Typography>
            </Paper>
        );
    }

    return (
        <Paper className={classes.paper} elevation={2}>
            <SectionBanner leftSideText="Admin - Manage Sales" />
            <div className={classes.section}>
                <div className={classes.sectionContent}>
                    {/* Live/Upcoming Sales */}
                    <Paper>
                        <Table className={classes.table}>
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        className={classes.tableCellHeader}
                                        colSpan={2}
                                    >
                                        <Typography
                                            className={classes.tableHeader}
                                            variant="subtitle1"
                                        >
                                            Live/Upcoming Sales
                                        </Typography>
                                    </TableCell>
                                    <TableCell colSpan={columnCount - 2}>
                                        <div className={classes.buttonToolbar}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="default"
                                                component={viewDeploysLink}
                                            >
                                                <OpenInNew />
                                                &nbsp;View Deploys
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="default"
                                                onClick={onClickPublishButton}
                                                disabled={isPublishing}
                                            >
                                                <Publish />
                                                &nbsp;Publish
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="default"
                                                component={auctionCreateLink}
                                            >
                                                <AddCircleOutline />
                                                &nbsp;Create New
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                                {renderColumnHeaders()}
                            </TableHead>
                            {renderAuctionRows(
                                onSale,
                                "No upcoming/live sales to display."
                            )}
                        </Table>
                    </Paper>

                    {renderSimpleTable(
                        "Draft Sales",
                        drafts,
                        "No draft sales to display."
                    )}

                    {renderSimpleTable(
                        "Closed Sales",
                        pastSales,
                        "No closed sales to display.",
                        true
                    )}
                </div>
            </div>
        </Paper>
    );
}

export default withStyles(styles)(ManageAuctionList);
